import * as _ from 'lodash';

import * as Types from './types';

// We treat these specially, for example by automatically
// joining using them.
// TODO: it would be much better to handle this with the type system!
export const SPECIAL_ID_COLUMN_NAMES = ['id', 'uuid'];

export function isIdColumnName(colName: string) {
  return SPECIAL_ID_COLUMN_NAMES.includes(colName.toLowerCase());
}
export type WBType = {
  wb_type: string;
  params?: {[key: string]: any};
};

export type ConstWBType<T = any> = WBType & {
  wb_type: 'const';
  params: {
    is_set?: boolean;
    val: T;
  };
};

export const isConstWBType = (t: WBType): t is ConstWBType => {
  return t.wb_type === 'const';
};

export type PythonObjectWBType = WBType & {
  // 'object' kept for legacy reasons
  wb_type: 'pythonObject' | 'object';
  params: {
    class_name: string;
  };
};

export const isPythonObjectWBType = (t: WBType): t is PythonObjectWBType => {
  return t.wb_type === 'pythonObject' || t.wb_type === 'object';
};

export type TypedDictWBType = WBType & {
  // dictionary kept for legacy reasons
  wb_type: 'dictionary' | 'typedDict';
  params: {
    type_map: {[columnName: string]: WBType};
  };
};

export const isTypedDictWBType = (t: WBType): t is TypedDictWBType => {
  return t.wb_type === 'dictionary' || t.wb_type === 'typedDict';
};

export type ListWBType = WBType & {
  wb_type: 'list';
  params: {
    element_type: WBType;
    length?: number;
  };
};

export const isListWBType = (t: WBType): t is ListWBType => {
  return t.wb_type === 'list';
};

export type UnionWBType = WBType & {
  wb_type: 'union';
  params: {
    allowed_types: WBType[];
  };
};

export const isUnionWBType = (t: WBType): t is UnionWBType => {
  return t.wb_type === 'union';
};

export type ForeignKeyWBType = WBType & {
  // 'wandb.TableForeignKey' kept for legacy reasons
  wb_type: 'wandb.TableForeignKey' | 'foreignKey';
  params: {
    element_type: WBType;
    length?: number;
  };
};

export const isForeignKeyWBType = (t: WBType): t is ForeignKeyWBType => {
  return t.wb_type === 'wandb.TableForeignKey' || t.wb_type === 'foreignKey';
};

export type ForeignIndexWBType = WBType & {
  // 'wandb.TableForeignIndex' kept for legacy reasons
  wb_type: 'wandb.TableForeignIndex' | 'foreignIndex';
  params: {
    element_type: WBType;
    length?: number;
  };
};

export const isForeignIndexWBType = (t: WBType): t is ForeignIndexWBType => {
  return (
    t.wb_type === 'wandb.TableForeignIndex' || t.wb_type === 'foreignIndex'
  );
};

export type PrimaryKeyWBType = WBType & {
  // 'wandb.TablePrimaryKey' kept for legacy reasons
  wb_type: 'wandb.TablePrimaryKey' | 'primaryKey';
  params: {
    element_type: WBType;
    length?: number;
  };
};

export const isPrimaryKeyWBType = (t: WBType): t is PrimaryKeyWBType => {
  return t.wb_type === 'wandb.TablePrimaryKey' || t.wb_type === 'primaryKey';
};

export type NDArrayWBType = WBType & {
  wb_type: 'ndarray';
  params: {
    serialization_path: {
      path: string;
      key: string;
    };
    shape: number[];
  };
};

export const isNDArrayWBType = (t: WBType): t is NDArrayWBType => {
  return t.wb_type === 'ndarray';
};

export type ClassesIdWBType = WBType & {
  // 'wandb.Classes_id' kept for legacy reasons
  wb_type: 'wandb.Classes_id' | 'classesId';
};

export const isClassesIdWBType = (t: WBType): t is ClassesIdWBType => {
  return t.wb_type === 'wandb.Classes_id' || t.wb_type === 'ClassesId';
};

export type TableWBType = WBType & {
  // 'wandb.Table' kept for legacy reasons
  wb_type: 'wandb.Table' | 'table';
  // TODO: add params if we ever want to pull param data
};

export const isTableWBType = (t: WBType): t is TableWBType => {
  return t.wb_type === 'wandb.Table' || t.wb_type === 'table';
};

export type ImageFileWBType = WBType & {
  // 'wandb.Image' kept for legacy reasons
  wb_type: 'wandb.Image' | 'image-file';
  // TODO: add params if we ever want to pull param data
  params?: {
    box_layers?: ConstWBType<{[layerName: string]: string[]}>;
    mask_layers?: ConstWBType<{[layerName: string]: string[]}>;
    class_map?: ConstWBType<{[id: string]: string}>;
    box_score_keys?: ConstWBType<string[]>;
  };
};

export const isImageFileWBType = (t: WBType): t is ImageFileWBType => {
  return t.wb_type === 'wandb.Image' || t.wb_type === 'image-file';
};

export type VideoFileWBType = WBType & {
  wb_type: 'video-file';
  // TODO: add params if we ever want to pull param data
};

export const isVideoFileWBType = (t: WBType): t is VideoFileWBType => {
  return t.wb_type === 'video-file';
};

export type AudioFileWBType = WBType & {
  wb_type: 'audio-file';
  // TODO: add params if we ever want to pull param data
};

export const isAudioFileWBType = (t: WBType): t is AudioFileWBType => {
  return t.wb_type === 'audio-file';
};

export type HtmlFileWBType = WBType & {
  wb_type: 'html-file';
  // TODO: add params if we ever want to pull param data
};

export const isHtmlFileWBType = (t: WBType): t is HtmlFileWBType => {
  return t.wb_type === 'html-file';
};

export type BokehFileWBType = WBType & {
  wb_type: 'bokeh-file';
  // TODO: add params if we ever want to pull param data
};

export const isBokehFileWBType = (t: WBType): t is BokehFileWBType => {
  return t.wb_type === 'bokeh-file';
};

export type Object3DFileWBType = WBType & {
  wb_type: 'object3D-file';
  // TODO: add params if we ever want to pull param data
};

export const isObject3DFileWBType = (t: WBType): t is Object3DFileWBType => {
  return t.wb_type === 'object3D-file';
};

export type MoleculeFileWBType = WBType & {
  wb_type: 'molecule-file';
  // TODO: add params if we ever want to pull param data
};

export const isMoleculeFileWBType = (t: WBType): t is MoleculeFileWBType => {
  return t.wb_type === 'molecule-file';
};

export type TimestampWBType = WBType & {
  wb_type: 'timestamp';
};

export const isTimestampWBType = (t: WBType): t is TimestampWBType => {
  return t.wb_type === 'timestamp';
};

export type JoinedTableWBType = WBType & {
  wb_type: 'joined-table';
  // TODO: add params if we ever want to pull param data
};

export const isJoinedTableWBType = (t: WBType): t is JoinedTableWBType => {
  return t.wb_type === 'joined-table';
};

export type PartitionedTableWBType = WBType & {
  wb_type: 'partitioned-table';
  // TODO: add params if we ever want to pull param data
};

export const isPartitionedTableWBType = (
  t: WBType
): t is PartitionedTableWBType => {
  return t.wb_type === 'partitioned-table';
};

export interface MediaTable {
  columns: Array<string | number>;
  data: any[][];
  column_types?: TypedDictWBType;
}

export interface MediaJoinedTable {
  table1: string;
  table2: string;
  join_key: string;
}

export interface MediaPartitionedTable {
  parts_path: string;
}

export type MediaType =
  | 'string'
  | 'number'
  | 'unknown'
  | 'boolean'
  | 'image-file'
  | 'audio-file'
  | 'html-file'
  | 'bokeh-file'
  | 'object3D-file'
  | 'molecule-file'
  | 'video-file';

function valToMediaType(val: any, colName: string | number): Types.Type {
  if (SPECIAL_ID_COLUMN_NAMES.includes(colName.toString())) {
    return 'id';
  } else if (typeof val === 'string' || val === null) {
    return 'string';
  } else if (typeof val === 'number') {
    return 'number';
  } else if (typeof val === 'boolean') {
    return 'boolean';
  } else if (_.isArray(val)) {
    if (val.length === 0) {
      return 'unknown';
    }
    // Use the first entry to determine type
    return {
      type: 'list',
      objectType: valToMediaType(val[0], colName),
    };
  } else if (_.isObject(val)) {
    if (
      // We don't use .type, but some old demo code did and I don't
      // want to break it
      (val as any).type === 'image-file'
    ) {
      return {type: 'image-file'};
    } else if (
      [
        'image-file',
        'video-file',
        'audio-file',
        'html-file',
        'bokeh-file',
        'object3D-file',
        'molecule-file',
      ].includes((val as any)._type)
    ) {
      return {type: (val as any)._type};
    }
  }
  return 'unknown';
}

function wbTypeToMediaType(t: WBType): Types.Type {
  if (t.wb_type === 'number') {
    return 'number';
  } else if (t.wb_type === 'string') {
    return 'string';
  } else if (t.wb_type === 'boolean') {
    return 'boolean';
  } else if (t.wb_type === 'any') {
    return 'any';
  } else if (t.wb_type === 'none') {
    return 'none';
  } else if (t.wb_type === 'unknown') {
    return 'unknown';
  } else if (t.wb_type === 'invalid') {
    return 'invalid';
  } else if (isConstWBType(t)) {
    return valToMediaType(t.params.val, '');
  } else if (isTypedDictWBType(t)) {
    const propertyTypes: {[key: string]: Types.Type} = {};
    const types = t.params.type_map;
    Object.keys(types).forEach(pType => {
      propertyTypes[pType] = wbTypeToMediaType(types[pType]);
    });
    return {
      type: 'typedDict',
      propertyTypes,
    };
  } else if (isListWBType(t)) {
    if (t.params?.length != null) {
      return Types.listWithLength(
        t.params?.length,
        wbTypeToMediaType(t.params.element_type)
      );
    } else {
      return Types.list(wbTypeToMediaType(t.params.element_type));
    }
    // TODO: Re-enable
    // } else if (isUnionWBType(t)) {
    //   return {
    //     type: 'union',
    //     members: t.params.allowed_types.map(wbTypeToMediaType),
    //   };
  } else if (isForeignIndexWBType(t)) {
    // For foreign indexes, they will always be integers. We should treat them as such.
    // TODO: Change this to index type  with a panel eventually?
    return 'number';
  } else if (isForeignKeyWBType(t)) {
    // For foreign keys, they will always be strings. We should treat them as such.
    // TODO: Change this to foreign key type with a panel eventually?
    return 'string';
  } else if (isPrimaryKeyWBType(t)) {
    // For primary keys, they will always be strings. We should treat them as such.
    // TODO: Change this to primary key type  with a panel eventually?
    return 'string';
  } else if (isNDArrayWBType(t)) {
    return {
      type: 'ndarray',
      serializationPath: t.params.serialization_path,
      shape: t.params.shape,
    };
  } else if (isPythonObjectWBType(t)) {
    // Legacy Class-based types (only applicable "pre-launch")
    if (t.params.class_name === 'Video') {
      return {type: 'video-file'};
    } else if (t.params.class_name === 'Audio') {
      return {type: 'audio-file'};
    } else if (t.params.class_name === 'Html') {
      return {type: 'html-file'};
    } else if (t.params.class_name === 'Bokeh') {
      return {type: 'bokeh-file'};
    } else if (t.params.class_name === 'Object3D') {
      return {type: 'object3D-file'};
    } else if (t.params.class_name === 'Molecule') {
      return {type: 'molecule-file'};
    }
    return 'unknown';
  } else if (isClassesIdWBType(t)) {
    // This type is not used in the application yet, so returning unknown for now.
    // Honestly, we should remove this type from python client and just use table links
    return 'unknown';
  } else if (isImageFileWBType(t)) {
    const hasClassInfo = t.params?.class_map?.params?.val != null;
    if (!hasClassInfo) {
      return {
        type: 'image-file',
      };
    } else {
      return {
        type: 'image-file',
        boxLayers: t.params?.box_layers?.params?.val ?? {},
        boxScoreKeys: t.params?.box_score_keys?.params?.val ?? [],
        maskLayers: t.params?.mask_layers?.params?.val ?? {},
        classMap: t.params?.class_map?.params?.val ?? {},
      } as Types.ImageType;
    }
  } else if (isTableWBType(t)) {
    return {type: 'table', columnTypes: {}};
  } else if (isVideoFileWBType(t)) {
    return {type: 'video-file'};
  } else if (isAudioFileWBType(t)) {
    return {type: 'audio-file'};
  } else if (isHtmlFileWBType(t)) {
    return {type: 'html-file'};
  } else if (isBokehFileWBType(t)) {
    return {type: 'bokeh-file'};
  } else if (isObject3DFileWBType(t)) {
    return {type: 'object3D-file'};
  } else if (isMoleculeFileWBType(t)) {
    return {type: 'molecule-file'};
  } else if (isTimestampWBType(t)) {
    return {type: 'timestamp', unit: t.params?.unit || 'ms'};
  } else if (isPartitionedTableWBType(t) || isJoinedTableWBType(t)) {
    // This should never actually be the case
    return 'unknown';
  } else if (isUnionWBType(t)) {
    return Types.union(t.params.allowed_types.map(wbTypeToMediaType));
  }

  return 'unknown';
}

// wandb <= 0.12.9 did not protect against floating point columns
// when dataframes were used in the construction. This is a small patch
// to fix this mistake at runtime. Fixed in https://github.com/wandb/client/pull/3113
export const tableNeedsFloatColumnConversion = (table: MediaTable): boolean => {
  return _.isEqual(
    new Set(_.keys(table.column_types?.params?.type_map ?? {})),
    new Set((table.columns ?? []).map(c => `${c}.0`))
  );
};

export const detectColumnTypes = (table: MediaTable): Types.Type[] => {
  const useFloatConversion = tableNeedsFloatColumnConversion(table);
  const types = table.columns.map((c, i): Types.Type => {
    let res: Types.Type | null = null;
    if (SPECIAL_ID_COLUMN_NAMES.includes(c.toString())) {
      return 'id';
    }
    const typeMapCol = useFloatConversion ? `${c}.0` : c;
    if (table.column_types && table.column_types.params.type_map[typeMapCol]) {
      res = wbTypeToMediaType(table.column_types.params.type_map[typeMapCol]);
    }
    // If we are unable to resolve a type, or it is resolving to an "open" type, infer from data
    if (
      res == null ||
      res === 'unknown' ||
      res === 'any' ||
      (Types.isUnion(res) &&
        res.members.some(mem => mem === 'any' || mem === 'unknown'))
    ) {
      if (table.data.length > 0) {
        res = valToMediaType(table.data[0][i], c);
      } else {
        res = 'unknown';
      }
    }
    return res;
  });
  return types;
};

// TODO: agg types are duplicated everywhere
export const agg = (
  type: 'concat' | 'max' | 'min' | 'avg' | undefined,
  data: any[]
) => {
  if (type == null || type === 'concat') {
    return data;
  } else if (type === 'max') {
    return _.max(data);
  } else if (type === 'min') {
    return _.min(data);
  } else {
    return _.sum(data) / data.length;
  }
};
